import axios from 'axios';
import { BASE_URL } from '../constants/common';
import {ROUTES} from '../constants/login'
import { setLoginInformation } from '../slices/commonReducer';
import store from '../store'
import { googleLogout } from '@react-oauth/google';
import WebSocketService from './websocketService';

const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 60000,
})

// Add variables to prevent continuous refresh calls
let isRefreshing = false;
let refreshSubscribers = [];

// Helper function to handle queued requests
const onRefreshed = (token) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

// Helper function to handle logout and navigation
const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    googleLogout();
    
    // Set session storage to show toast message (optional)
    if (!sessionStorage.getItem("showLoginToast")) {
        sessionStorage.setItem("showLoginToast", "You have been logged out, Please try login again");
    }
    
    // Navigate to login page
    window.location.href = '/login';
};

instance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        } else {
            delete config.headers.Authorization;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Improved refresh token interceptor
instance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            // If already refreshing, queue the request
            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshSubscribers.push((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        resolve(instance(originalRequest));
                    });
                });
            }
            
            originalRequest._retry = true;
            isRefreshing = true;
            
            try {
                const newToken = await refreshAccessToken();
                
                // Update the original request with new token
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                // Notify all queued requests
                onRefreshed(newToken);
                
                return instance(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                
                // Handle logout and navigation on refresh failure
                handleLogout();
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false; // Reset flag
            }
        }
        
        return Promise.reject(error);
    }
);

const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refresh');
    
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }
    
    try {
        // Remove old token before refreshing
        localStorage.removeItem('token');
        
        const req_body = { refresh: refreshToken };
        if (localStorage.getItem('preface_admin_fcm_token') !== null) {
            req_body.fcm_token = localStorage.getItem('preface_admin_fcm_token');
        }
        
        // Use a separate axios instance for refresh to avoid interceptor loops
        const refreshResponse = await axios.post(`${BASE_URL}${ROUTES.REFRESH_TOKEN}`, req_body);
        const { token, refresh } = refreshResponse.data.data;
        
        localStorage.setItem("token", token);
        localStorage.setItem("refresh", refresh);

        const dispatch = store.dispatch;
        WebSocketService.connect(dispatch);
            
        return token;
    } catch (error) {
        console.error('Refresh token error:', error);
        
        // If refresh fails, it means refresh token is also expired/invalid
        throw error;
    }
};

export const signIn = async (credentails) => {
    try{
    const response = await instance.post(ROUTES.SIGN_IN, credentails );
    const { token, refresh, data: user_data  } = response.data
    localStorage.setItem('token', token)
    localStorage.setItem('refresh', refresh)
    if (user_data) {
      const { name, email, id, role, permission_code, phone } = user_data
      let data = { name, email , id , role: role.name, role_id: role.id, permissions: permission_code, mobile: phone }
      store.dispatch(setLoginInformation(data))
    }

    const dispatch = store.dispatch;
    WebSocketService.connect(dispatch);
    
    return {status: true }
    } catch(err) {
      const msg = err?.response?.data?.message || 'Invalid Username or Password'
      return {status:false, message: msg };
    }
}

export const googleLogin = async (credentails) => {
  try{
  const response = await instance.post(ROUTES.GOOGLE_SIGN_IN, credentails );
  const { token, refresh, data: user_data  } = response.data
  if (token) {
    localStorage.setItem('token', token)
    localStorage.setItem('refresh', refresh)
    if (user_data) {
      const { name, email, id, role, permission_code } = user_data
      let data = { name, email , id , role: role.name, permissions: permission_code }
      store.dispatch(setLoginInformation(data))
    }
  }
  
    const dispatch = store.dispatch;
    WebSocketService.connect(dispatch);
  
  return {status: true }
  } catch(err) {
      const msg = err?.response?.data?.message || 'User verification pending'
      return {status:false, message: msg };
  }
}

export const signOut = async () => {
  try{
    const refresh = localStorage.getItem('refresh')
    await instance.post(ROUTES.SIGN_OUT , {refresh});
    localStorage.removeItem('token')
    localStorage.removeItem('refresh')
    WebSocketService.disconnect();
    googleLogout()
    return true
    } catch(err) {
        return false;
    }
}

export default instance